"use client";

import { useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { countryName, flagUrl, normalizeForSearch } from "@/lib/countries";
import { useCountryStore } from "@/lib/country-store";
import { useLocale, useMessages } from "@/lib/i18n/client";
import messages from "./countries-dialog.i18n";

// Paso obligatorio al suscribirse al newsletter: elegir explícitamente una o
// varias carteleras de país (nada viene marcado), con buscador. Se monta solo
// mientras está abierto, así cada apertura parte sin selección.
export function NewsletterCountriesDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: (countries: string[]) => void;
  onCancel: () => void;
}) {
  const t = useMessages(messages);
  const lang = useLocale();
  const available = useCountryStore((s) => s.available);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  // La lista es un desplegable: se abre al enfocar o escribir en el buscador
  // y se cierra al elegir, al salir del campo o con Escape.
  const [listOpen, setListOpen] = useState(false);

  // Países con cartelera, con su nombre en el idioma de la página y ordenados.
  const countries = useMemo(
    () =>
      available
        .map((code) => ({ code, name: countryName(code, lang) }))
        .sort((a, b) => a.name.localeCompare(b.name, lang)),
    [available, lang],
  );
  const filtered = countries.filter((c) => normalizeForSearch(c.name).includes(normalizeForSearch(search)));

  const toggle = (code: string) =>
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const pick = (code: string) => {
    toggle(code);
    setSearch("");
    setListOpen(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        onEscapeKeyDown={(e) => {
          // Escape con la lista abierta solo la cierra, no el modal.
          if (listOpen) {
            e.preventDefault();
            setListOpen(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((code) => (
              <span
                key={code}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary/10 py-1 pl-2.5 pr-1.5 text-sm font-medium"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={flagUrl(code)} alt="" className="h-3 w-4 rounded-[2px] object-cover" />
                {countryName(code, lang)}
                <button
                  type="button"
                  onClick={() => toggle(code)}
                  aria-label={t.remove(countryName(code, lang))}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <Command shouldFilter={false} className="relative overflow-visible rounded-xl border border-border [&_[cmdk-input-wrapper]]:border-b-0">
          <CommandInput
            placeholder={t.search}
            value={search}
            onValueChange={(value) => {
              setSearch(value);
              setListOpen(true);
            }}
            onFocus={() => setListOpen(true)}
            onBlur={() => setListOpen(false)}
            onClick={() => setListOpen(true)}
          />
          <CommandList
            hidden={!listOpen}
            // Sin esto, el clic en un país saca el foco del buscador y la
            // lista se cierra antes de registrar la elección.
            onMouseDown={(e) => e.preventDefault()}
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 rounded-xl border border-border bg-popover shadow-lg"
          >
            <CommandGroup>
              {filtered.map((c) => {
                const active = selected.includes(c.code);
                return (
                  <CommandItem key={c.code} value={c.code} onSelect={() => pick(c.code)} className="text-sm">
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", active ? "opacity-100" : "opacity-0")} />
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={flagUrl(c.code)} alt="" className="mr-2 h-3 w-4 rounded-[2px] object-cover" />
                    {c.name}
                  </CommandItem>
                );
              })}
              {filtered.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t.empty}</p>}
            </CommandGroup>
          </CommandList>
        </Command>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t.cancel}
          </Button>
          <Button type="button" disabled={selected.length === 0} onClick={() => onConfirm(selected)}>
            {t.confirm(selected.length)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
